import {
    AttractionFactCandidateRO,
    AttractionResearchRunRO,
    AttractionStoryBrief,
    CreateAttractionFactDto,
    DeepenAttractionFactsDto,
    SaveAttractionStoryBriefDto,
    StartAttractionResearchDto,
    UpdateAttractionFactDto,
} from '@routellers/types/dist/city-attraction/attraction-fact';
import {
    AttractionTriageReportRO,
    TriageAttractionsDto,
} from '@routellers/types/dist/city-attraction/attraction-triage';
import { CatalogRestorePreviewRO } from '@routellers/types/dist/city-collection/city-collection.ro';
import { CityAttractionRO } from '@routellers/types/dist/city-attraction/city-attraction.ro';
import { CreateCityAttractionDto } from '@routellers/types/dist/city-attraction/create-city-attraction.dto';
import { GenerateAttractionAudioDto } from '@routellers/types/dist/city-attraction/generate-attraction-audio.dto';
import { GenerateAttractionAudioRO } from '@routellers/types/dist/city-attraction/generate-attraction-audio.ro';
import { ModerateCityAttractionDto } from '@routellers/types/dist/city-attraction/moderate-city-attraction.dto';
import { UpdateCityAttractionDto } from '@routellers/types/dist/city-attraction/update-city-attraction.dto';
import { UpsertCityAttractionArtifactDto } from '@routellers/types/dist/city-attraction/upsert-city-attraction-artifact.dto';
import {
    GenerateAttractionContentDto,
    UpsertCityAttractionLocaleDto,
} from '@routellers/types/dist/city-attraction/upsert-city-attraction-locale.dto';
import { GenerateAiCoverDto } from '@routellers/types/dist/shared/ai-cover';
import {
    CityPipelineRunRO,
    StartCityPipelineDto,
} from '@routellers/types/dist/shared/city-pipeline';
import {
    CityGapAuditDto,
    CityGapAuditRO,
    CityResearchDto,
    CityResearchReportRO,
} from '@routellers/types/dist/shared/city-research';
import {
    ContentBatchReportRO,
    GenerateContentBatchDto,
} from '@routellers/types/dist/shared/content-batch';
import { DeepenEntityDto, DeepenReportRO } from '@routellers/types/dist/shared/editorial-research';
import { FillCompleteDto, FillCompleteReportRO } from '@routellers/types/dist/shared/fill-complete';
import { QueryDto } from '@routellers/types/dist/shared/query.dto';
import { QueryRO } from '@routellers/types/dist/shared/query.ro';
import {
    EnrichWikidataDto,
    WikidataEnrichmentReportRO,
} from '@routellers/types/dist/shared/wikidata-enrichment';
import {
    WikidataCityImportDto,
    WikidataCityImportReportRO,
} from '@routellers/types/dist/shared/wikidata-import';

import { ApiMethodDeclaration } from '..';

const query: ApiMethodDeclaration<QueryDto, QueryRO<CityAttractionRO>> = (params) => ({
    url: '/admin/city-attractions',
    method: 'GET',
    params,
});

const getOne: ApiMethodDeclaration<number, CityAttractionRO> = (id) => ({
    url: `/admin/city-attractions/${id}`,
    method: 'GET',
});

const create: ApiMethodDeclaration<{ data: CreateCityAttractionDto }, CityAttractionRO> = ({
    data,
}) => ({
    url: '/admin/city-attractions',
    method: 'POST',
    data,
});

const update: ApiMethodDeclaration<
    { id: number; data: UpdateCityAttractionDto },
    CityAttractionRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}`,
    method: 'PATCH',
    data,
});

const moderate: ApiMethodDeclaration<
    { id: number; data: ModerateCityAttractionDto },
    CityAttractionRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/moderate`,
    method: 'POST',
    data,
});

const generateContent: ApiMethodDeclaration<
    { id: number; data: GenerateAttractionContentDto },
    CityAttractionRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/generate-content`,
    method: 'POST',
    data,
});

const deepen: ApiMethodDeclaration<
    { id: number; data: DeepenEntityDto },
    DeepenReportRO<CityAttractionRO>
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/deepen`,
    method: 'POST',
    data,
});

const fillComplete: ApiMethodDeclaration<
    { id: number; data: FillCompleteDto },
    FillCompleteReportRO<CityAttractionRO>
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/fill-complete`,
    method: 'POST',
    data,
});

const generateAudio: ApiMethodDeclaration<
    { id: number; data: GenerateAttractionAudioDto },
    GenerateAttractionAudioRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/generate-audio`,
    method: 'POST',
    data,
});

const upsertLocale: ApiMethodDeclaration<
    { id: number; data: UpsertCityAttractionLocaleDto },
    CityAttractionRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/locales`,
    method: 'PATCH',
    data,
});

const replaceArtifacts: ApiMethodDeclaration<
    { id: number; data: { artifacts: UpsertCityAttractionArtifactDto[] } },
    CityAttractionRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/artifacts`,
    method: 'PUT',
    data,
});

const enrichWikidata: ApiMethodDeclaration<
    { data: EnrichWikidataDto },
    WikidataEnrichmentReportRO
> = ({ data }) => ({
    url: '/admin/city-attractions/maintenance/enrich-wikidata',
    method: 'POST',
    data,
});

const importWikidataCity: ApiMethodDeclaration<
    { data: WikidataCityImportDto },
    WikidataCityImportReportRO
> = ({ data }) => ({
    url: '/admin/city-attractions/maintenance/import-wikidata-city',
    method: 'POST',
    data,
});

const generateContentBatch: ApiMethodDeclaration<
    { data: GenerateContentBatchDto },
    ContentBatchReportRO
> = ({ data }) => ({
    url: '/admin/city-attractions/maintenance/generate-content-batch',
    method: 'POST',
    data,
});

const generateCover: ApiMethodDeclaration<
    { id: number; data: GenerateAiCoverDto },
    CityAttractionRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/generate-cover`,
    method: 'POST',
    data,
});

const startPipeline: ApiMethodDeclaration<{ data: StartCityPipelineDto }, CityPipelineRunRO> = ({
    data,
}) => ({
    url: '/admin/city-attractions/maintenance/pipeline',
    method: 'POST',
    data,
});

const pipelineStatus: ApiMethodDeclaration<number, CityPipelineRunRO> = (id) => ({
    url: `/admin/city-attractions/maintenance/pipeline/${id}`,
    method: 'GET',
});

const cancelPipeline: ApiMethodDeclaration<number, CityPipelineRunRO> = (id) => ({
    url: `/admin/city-attractions/maintenance/pipeline/${id}/cancel`,
    method: 'POST',
});

const latestPipeline: ApiMethodDeclaration<void, CityPipelineRunRO | null> = () => ({
    url: '/admin/city-attractions/maintenance/pipeline/latest',
    method: 'GET',
});

const researchCity: ApiMethodDeclaration<{ data: CityResearchDto }, CityResearchReportRO> = ({
    data,
}) => ({
    url: '/admin/city-attractions/maintenance/research-city',
    method: 'POST',
    data,
});

const triage: ApiMethodDeclaration<{ data: TriageAttractionsDto }, AttractionTriageReportRO> = ({
    data,
}) => ({
    url: '/admin/city-attractions/maintenance/triage',
    method: 'POST',
    data,
});

const remove: ApiMethodDeclaration<number, { success: boolean }> = (id) => ({
    url: `/admin/city-attractions/${id}`,
    method: 'DELETE',
});

const archive: ApiMethodDeclaration<
    { id: number; data: { archived?: boolean } },
    CityAttractionRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/archive`,
    method: 'POST',
    data,
});

const listFacts: ApiMethodDeclaration<number, AttractionFactCandidateRO[]> = (id) => ({
    url: `/admin/city-attractions/${id}/facts`,
    method: 'GET',
});

const createFact: ApiMethodDeclaration<
    { id: number; data: CreateAttractionFactDto },
    AttractionFactCandidateRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/facts`,
    method: 'POST',
    data,
});

const updateFact: ApiMethodDeclaration<
    { factId: number; data: UpdateAttractionFactDto },
    AttractionFactCandidateRO
> = ({ factId, data }) => ({
    url: `/admin/city-attractions/facts/${factId}`,
    method: 'PATCH',
    data,
});

const removeFact: ApiMethodDeclaration<number, { success: boolean }> = (factId) => ({
    url: `/admin/city-attractions/facts/${factId}`,
    method: 'DELETE',
});

const startFactResearch: ApiMethodDeclaration<
    { id: number; data: StartAttractionResearchDto },
    AttractionResearchRunRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/facts/research`,
    method: 'POST',
    data,
});

const factResearchRuns: ApiMethodDeclaration<number, AttractionResearchRunRO[]> = (id) => ({
    url: `/admin/city-attractions/${id}/facts/research-runs`,
    method: 'GET',
});

const deepenFacts: ApiMethodDeclaration<
    { id: number; data: DeepenAttractionFactsDto },
    AttractionResearchRunRO
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/facts/deepen`,
    method: 'POST',
    data,
});

const dedupeFacts: ApiMethodDeclaration<number, AttractionResearchRunRO> = (id) => ({
    url: `/admin/city-attractions/${id}/facts/dedupe`,
    method: 'POST',
});

const draftScoreFacts: ApiMethodDeclaration<number, AttractionResearchRunRO> = (id) => ({
    url: `/admin/city-attractions/${id}/facts/draft-score`,
    method: 'POST',
});

const previewCatalogRestores: ApiMethodDeclaration<
    { data: { cityId: number } },
    CatalogRestorePreviewRO
> = ({ data }) => ({
    url: '/admin/city-attractions/maintenance/preview-catalog-restores',
    method: 'POST',
    data,
});

const gapAudit: ApiMethodDeclaration<{ data: CityGapAuditDto }, CityGapAuditRO> = ({ data }) => ({
    url: '/admin/city-attractions/maintenance/gap-audit',
    method: 'POST',
    data,
});

const getStoryBrief: ApiMethodDeclaration<number, { brief: AttractionStoryBrief | null }> = (
    id,
) => ({
    url: `/admin/city-attractions/${id}/story-brief`,
    method: 'GET',
});

const saveStoryBrief: ApiMethodDeclaration<
    { id: number; data: SaveAttractionStoryBriefDto },
    { brief: AttractionStoryBrief }
> = ({ id, data }) => ({
    url: `/admin/city-attractions/${id}/story-brief`,
    method: 'PUT',
    data,
});

export const cityAttractionsApiDeclaration = {
    query,
    getOne,
    create,
    update,
    moderate,
    generateContent,
    deepen,
    fillComplete,
    generateAudio,
    upsertLocale,
    replaceArtifacts,
    enrichWikidata,
    importWikidataCity,
    startPipeline,
    pipelineStatus,
    cancelPipeline,
    latestPipeline,
    researchCity,
    generateContentBatch,
    generateCover,
    triage,
    remove,
    archive,
    listFacts,
    createFact,
    updateFact,
    removeFact,
    startFactResearch,
    factResearchRuns,
    deepenFacts,
    dedupeFacts,
    draftScoreFacts,
    gapAudit,
    previewCatalogRestores,
    getStoryBrief,
    saveStoryBrief,
};
